import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../utils/api';
import './Enquiry.css';

// Standalone, mobile-first enquiry landing page — for ANYTHING:
// therapy, courses, masterclasses, consultations, or a general question.
// Routes: /enquiry  and  /enquiry/:topic  (preselects a topic).
// Renders WITHOUT the site chrome (see the `bare` flag in App.js) so it works
// as a clean, shareable link for campaigns / WhatsApp / ads.
//
// It's a thin lead-capture wrapper over the existing /api/enquiries.php
// endpoint (source = 'enquiry-<topic>'), so submissions land in
// /admin/enquiries alongside the rest. The services themselves are paid —
// this page only captures interested users, it does NOT imply anything is free.

// Real social proof, sourced from the live site (TrustStrip).
const STATS = [
  { value: '40+', label: 'Years of practice' },
  { value: '4000+', label: 'Lives transformed' },
  { value: '4.9★', label: 'Client rating' },
];

const TOPICS = [
  { slug: 'therapy',      label: 'Therapy session',   hint: 'Hypnotherapy, counselling & healing' },
  { slug: 'courses',      label: 'Course / training', hint: 'Certified programs & diplomas' },
  { slug: 'masterclass',  label: 'Masterclass',       hint: 'Short intensive workshops' },
  { slug: 'consultation', label: 'Consultation',      hint: 'Talk to a practitioner first' },
  { slug: 'other',        label: 'Something else',    hint: 'Anything else you have in mind' },
];

function Enquiry() {
  const { topic: topicParam } = useParams();

  // Preselect from the URL if it matches a known topic; else leave unset.
  const initial = useMemo(() => {
    const match = TOPICS.find((t) => t.slug === (topicParam || '').toLowerCase());
    return match ? match.slug : '';
  }, [topicParam]);

  const [topic, setTopic] = useState(initial);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setTopic(initial); }, [initial]);

  useEffect(() => {
    document.title = 'Enquiry · Mind Spa';
  }, []);

  const selected = TOPICS.find((t) => t.slug === topic) || null;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please fill in your name, email and phone.');
      return;
    }
    if (!topic) {
      setError('Please choose what you’re enquiring about.');
      return;
    }
    setSubmitting(true);
    try {
      // enquiries.php requires a non-empty message — always send one.
      const label = selected ? selected.label : 'General enquiry';
      const message = form.message.trim()
        ? `Enquiry about ${label}.\n\n${form.message.trim()}`
        : `Enquiry about ${label}.`;
      await api.submitEnquiry({
        name: form.name,
        email: form.email,
        phone: form.phone,
        topic: label,
        message,
        source: `enquiry-${topic}`,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="enq">
      <div className="enq__card">
        {/* Top bar: logo left, home button right */}
        <div className="enq__topbar">
          <Link to="/" className="enq__logo" aria-label="Mind Spa — home">
            <img src="/logo.png" alt="" className="enq__logo-img" width="1063" height="1152" decoding="async" />
            <span className="enq__logo-brand">Mind Spa</span>
          </Link>
          <Link to="/" className="enq__home-btn">
            <span>Home</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="enq__brand">
          <span className="enq__eyebrow">Enquire</span>
        </div>

        {done ? (
          <div className="enq__done">
            <div className="enq__tick" aria-hidden="true">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h1 className="enq__done-title">Message sent!</h1>
            <p className="enq__done-text">
              Thanks, {form.name.split(' ')[0] || 'there'} — we’ve received your enquiry
              {selected ? <> about <strong>{selected.label.toLowerCase()}</strong></> : null}.
              We’ll reply to <strong>{form.email}</strong> within a working day.
            </p>
            <Link to="/" className="btn-outline enq__home">Back to home</Link>
          </div>
        ) : (
          <>
            <header className="enq__head">
              <h1 className="enq__title">How can we help?</h1>
              <p className="enq__subtitle">
                Tell us what you’re looking for — a therapy session, a course, a
                masterclass, or anything else. Send us a message and we’ll get back to you.
              </p>
            </header>

            <form className="enq__form" onSubmit={handleSubmit} noValidate>
              {error && <div className="enq__error" role="alert">{error}</div>}

              {/* Topic selector */}
              <fieldset className="enq__topics">
                <legend className="enq__field-label">I’m enquiring about</legend>
                <div className="enq__topic-grid">
                  {TOPICS.map((t) => (
                    <button
                      type="button"
                      key={t.slug}
                      className={`enq__topic${topic === t.slug ? ' is-selected' : ''}`}
                      aria-pressed={topic === t.slug}
                      onClick={() => setTopic(t.slug)}
                    >
                      <span className="enq__topic-label">{t.label}</span>
                      <span className="enq__topic-hint">{t.hint}</span>
                    </button>
                  ))}
                </div>
              </fieldset>

              <div className="enq__field">
                <label htmlFor="enq-name">Full name</label>
                <input id="enq-name" name="name" value={form.name} onChange={handleChange}
                       autoComplete="name" maxLength={120} placeholder="Your name" required />
              </div>

              <div className="enq__field">
                <label htmlFor="enq-email">Email</label>
                <input id="enq-email" name="email" type="email" value={form.email} onChange={handleChange}
                       autoComplete="email" maxLength={190} placeholder="you@example.com" required />
              </div>

              <div className="enq__field">
                <label htmlFor="enq-phone">Phone / WhatsApp</label>
                <input id="enq-phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
                       autoComplete="tel" maxLength={40} placeholder="+91…" required />
              </div>

              <div className="enq__field">
                <label htmlFor="enq-message">Your message <span>(optional)</span></label>
                <textarea id="enq-message" name="message" value={form.message} onChange={handleChange}
                          maxLength={4000} placeholder="Tell us a little about what you need, preferred timing, questions…" />
              </div>

              <button type="submit" className="btn-primary enq__submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send enquiry'}
              </button>

              <p className="enq__fineprint">
                No obligation — we usually reply within a working day.
              </p>
            </form>

            {/* Light social proof */}
            <section className="enq__proof">
              <div className="enq__stats">
                {STATS.map((s) => (
                  <div key={s.label} className="enq__stat">
                    <span className="enq__stat-value">{s.value}</span>
                    <span className="enq__stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
              <p className="enq__proof-lead">
                Trusted by thousands across India for hypnotherapy, counselling, and
                professional training — led by internationally certified practitioners.
              </p>
            </section>
          </>
        )}

        <footer className="enq__footer">
          <Link to="/" className="enq__footer-link">MindSpaIndia 2026</Link>
        </footer>
      </div>
    </div>
  );
}

export default Enquiry;
